import React, { useState } from "react"
import Badge from "../../../components/Badge";
import Moment from "moment"
import { GetApi } from "../../../api/api"
import Pending from "../../../components/Pending"
import "./featureCatalogTable.scss"

export interface IFeatureCatalog {
  name: string
  displayText: string
  role?: string
  currentVersion: string
  date: string
  id: string
  standard: string
  category: string
  sortKey: string
}

interface RenderSuccess {
  featureCatalogs: IFeatureCatalog[]
}

interface FeatureCatalogProps {
  onClick: (fc: IFeatureCatalog) => void
}

const FeatureCatalogTable = (props: FeatureCatalogProps) => {
  const [expandedStandards, setExpandedStandards] = useState([] as string[])

  const getFeatureCatalogs = async () =>
    GetApi()
      .then((api) => api.getAnyFeatureCatalogs(undefined, undefined, undefined, "4.211"))
      .then((featureCatalogs) =>
        featureCatalogs.map((fc) => ({
          name: fc.name,
          displayText: fc.displayText!,
          currentVersion: fc.version,
          date: Moment(fc.date).format("ll"),
          id: fc.id,
          standard: fc.standard,
          category: fc.category,
          sortKey: `${fc.category === 'Standard' ? '-' : fc.category}.${fc.name} ${fc.version}`
        }))
      )
      .then((e) => ({
        featureCatalogs: e
      }))

  const renderSuccess = (renderProps: RenderSuccess) => {
    const onRowClick = (catalog: IFeatureCatalog) => {
      props.onClick(catalog)
    }

    const mapByStandard = renderProps.featureCatalogs.reduce(
      (group: Map<string, IFeatureCatalog[]>, catalog: IFeatureCatalog) => {
        const { standard, currentVersion, category } = catalog
        const verDotPos = currentVersion.indexOf('.')
        const groupKey = `${standard}_${category}` + (verDotPos > -1 ? '.' + currentVersion.substring(0, verDotPos) : '')
        const catalogs = group.get(groupKey) ?? []
        catalogs!.push(catalog)
        group.set(
          groupKey,
          catalogs!.sort((a: IFeatureCatalog, b: IFeatureCatalog) => {
            return a.currentVersion < b.currentVersion ? 1 : -1
          })
        )
        return group
      },
      new Map<string, IFeatureCatalog[]>()
    )

    const getStandards = () => {
      const standards: string[] = []
      mapByStandard.forEach((catalogs, standard) => {
        standards.push(standard)
      })
      standards.sort((a: string, b: string) => {
        const skA = mapByStandard.get(a)?.at(0)?.sortKey
        const skB = mapByStandard.get(b)?.at(0)?.sortKey
        return skA?.localeCompare(skB ?? '') ?? 0
      })
      return standards
    }

    const showAllOrThree = (event: React.MouseEvent<HTMLElement>) => {
      const standard = event.currentTarget.dataset.route!
      const expandedList = [...expandedStandards]
      if (expandedStandards.includes(standard)) {
        expandedList.splice(expandedList.indexOf(standard), 1)
        setExpandedStandards(expandedList ? expandedList : [])
      } else {
        expandedList.push(standard)
        setExpandedStandards(expandedList)
      }
    }

    const handleOnClick = (event: React.MouseEvent<HTMLElement>) => {
      renderProps.featureCatalogs.map((catalog) => {
        if (catalog.id === event.currentTarget.dataset.route) {
          onRowClick(catalog)
        }
      })
    }

    return (
      <div>
        <table className="table table-borderless shadow table-hover">
          <thead className="bg-gray-light shadow-sm">
            <tr>
              <th scope="col">Feature catalog</th>
              <th scope="col">ID</th>
              <th scope="col">Date</th>
              <th />
            </tr>
          </thead>
          {getStandards().map((standard: string) => {
            return (
              <tbody className="bg-white" id="standardSection" key={standard}>
                {mapByStandard.get(standard)!.length < 4 ? (
                  <tr className="standardRow">
                    <th className="card-subtitle-2">
                      <span>{mapByStandard.get(standard)?.at(0)?.name}</span>
                      <Badge variant="secondary" className="ml-3" outline={true} pill={true}>{mapByStandard.get(standard)?.at(0)?.category}</Badge>
                    </th>
                    <td />
                    <td />
                    <th id="allShown">No more to show</th>
                  </tr>
                ) : (
                  <tr className="standardRow" data-route={standard} onClick={showAllOrThree}>
                    <th className="card-subtitle-2">
                      <span>{mapByStandard.get(standard)?.at(0)?.name}</span>
                      <Badge variant="secondary" className="ml-3" outline={true} pill={true}>{mapByStandard.get(standard)?.at(0)?.category}</Badge>
                    </th>
                    <td />
                    <td />
                    {expandedStandards.indexOf(standard) !== -1 ? (
                      <th id="showAll">
                        Show less
                        <i className="modus-icons" aria-hidden="true" id="expand">
                          expand_less
                        </i>
                      </th>
                    ) : (
                      <th id="showAll">
                        Show all
                        <i className="modus-icons" aria-hidden="true" id="expand">
                          expand_more
                        </i>
                      </th>
                    )}
                  </tr>
                )}
                {mapByStandard
                  .get(standard)
                  ?.slice(0, expandedStandards.includes(standard) ? mapByStandard.get(standard)?.length : 3)
                  .map((catalog) => {
                    return (
                      <tr id="catalogRow" data-route={catalog.id} key={catalog.id} onClick={handleOnClick}>
                        <th id="catalogVersion">{catalog.currentVersion}</th>
                        <td>{catalog.id}</td>
                        <td>{catalog.date}</td>
                        <td />
                      </tr>
                    )
                  })}
              </tbody>
            )
          })}
        </table>
      </div>
    )
  }

  return <Pending getData={getFeatureCatalogs} renderSuccess={renderSuccess} />
}

export default FeatureCatalogTable
